import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'actions';
import clsx from 'clsx';
import moment from 'moment';
import {
	Button, TextField, Card, Grid, CardHeader,
	CardContent, Tabs, Tab, Typography, Box, Divider,
	FormControlLabel, Checkbox
} from '@mui/material';
import _, { last, select } from 'underscore';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import DateTimePicker from 'components/DatePicker/DateTimePicker';
import TimePicker from 'components/DatePicker/TimePicker';

import { SetFormState, GetClustersNames, UpdateAcceptanceReports, GetReportLastUpdate } from '../../actions/ReportsAcceptanceActions';

const useStyles = makeStyles(theme => ({
	root: {},
}));

const ReportsAcceptanceDialogForm = (props) => {
	const { className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const userId = useSelector(state => state.session.user.id);
	const formState = useSelector(state => state.reportsAcceptanceReducer.formState);
	const activationDates = useSelector((state) => state.reportsAcceptanceReducer.activationDates);
	const lastUpdate = useSelector((state) => state.reportsAcceptanceReducer.lastUpdate);
	const clustersNames = useSelector((state) => state.reportsAcceptanceReducer.clustersNames);

	const reportOptions = [
		{
			value: "Cluster Acceptance",
			text: "Cluster Acceptance"
		},
		{
			value: "Rollback",
			text: "Rollback"
		}
	];

	useEffect(() => {
		if (
			!clustersNames
				.map(cluster => cluster.value)
				.includes(formState.values.selectedCluster)
		)
			setFormState({
				...formState,
				values: {
					...formState.values,
					selectedCluster: undefined
				}
			});
	}, [clustersNames]);

	useEffect(() => {
		if (formState.values.selectedReport
			&& formState.values.selectedActivationDate
			&& formState.values.selectedCluster
		)
			dispatch(GetReportLastUpdate(
				formState.values.selectedReport,
				formState.values.selectedActivationDate,
				formState.values.selectedCluster)
			);
	}, [
		formState.values.selectedReport,
		formState.values.selectedActivationDate,
		formState.values.selectedCluster
	]);

	const _onUpdateClick = () => {
		dispatch(UpdateAcceptanceReports(
			userId,
			formState.values.selectedReport,
			formState.values.selectedActivationDate,
			formState.values.selectedCluster));
	};

	const _onExportClick = () => {};

	const _handleActivationDateChange = event => {
		dispatch(GetClustersNames(event.target.value));
		_handleChange(event);
	};
	
	const _handleChange = event => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	useMemo(() => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				'clusterName': undefined
			}
		});
	}, [clustersNames]);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					<Card>
						<CardHeader title={"Reports Acceptance"} style={{ paddingBottom: 0 }} />
						<CardContent>
							<Grid container spacing={1}>
								<Grid item md={3} xs={3}>
									<SelectField
										id="selectedReport"
										name="selectedReport"
										hintText="Report"
										defaultValue=""
										md={3} xs={3} 
										menuItems={reportOptions}
										value={formState.values.selectedReport}
										onChange={_handleChange}
										sx={{
											'& .MuiInputBase-input': {
												backgroundColor: 'white',
												'&.Mui-disabled': {
													color: 'dimGray',
													WebkitTextFillColor: 'dimGray'
												}
											},
										}}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="selectedActivationDate"
										name="selectedActivationDate"
										hintText="Activation Date"
										defaultValue=""
										menuItems={activationDates || []}
										value={formState.values.selectedActivationDate}
										onChange={_handleActivationDateChange}
										sx={{
											'& .MuiInputBase-input': {
												backgroundColor: 'white',
												'&.Mui-disabled': {
													color: 'dimGray',
													WebkitTextFillColor: 'dimGray'
												}
											},
										}}
										disabled={!formState.values.selectedReport}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<SelectField
										id="selectedCluster"
										name="selectedCluster"
										hintText="Cluster Name"
										defaultValue=""
										menuItems={clustersNames || []}
										value={formState.values.selectedCluster}
										onChange={_handleChange}
										sx={{
											'& .MuiInputBase-input': {
												backgroundColor: 'white',
												'&.Mui-disabled': {
													color: 'dimGray',
													WebkitTextFillColor: 'dimGray'
												}
											},
										}}
										disabled={!formState.values.selectedActivationDate}
									/>
								</Grid>
								<Grid item md={3} xs={3} style={{ display: 'flex', justifyContent: 'space-around' }}>
									{/* {formState.values.selectedReport === "Cluster Acceptance" ?
										<Button
											variant={"contained"}
											color="primary"
											onClick={_onExportClick}
										>EXPORT</Button> : */}
										<Button
											size="small"
											variant="contained"
											color="primary"
											style={{ height: '100%', width: '50%' }}
											onClick={_onUpdateClick}
											disabled={!formState.values.selectedCluster || !formState.values.selectedActivationDate || !formState.values.selectedReport}
										>UPDATE</Button>
									{/* } */}
								</Grid>
								<Grid item md={9} xs={9}>
								</Grid>
								<Grid item md={3} xs={3} style={{ display: 'flex', justifyContent: 'space-around' }}>
									<Typography variant="overline" style={{ textAlign: 'center', width: '100%' }}>
										{(formState.values.selectedCluster
											&& formState.values.selectedReport
											&& formState.values.selectedActivationDate
											? (
												lastUpdate
												? "Last update: " + moment(lastUpdate).format('DD/MM/YYYY HH:mm:ss')
												: 'Not yet updated.')
											: '')}
									</Typography>
								</Grid>
								{/* {formState.values.selectedReport === "Cluster Acceptance" ?
									<Grid item spacing={2} md={6} xs={6} style={{ display: 'inline-flex', justifyContent: 'space-between', gap: '10px' }}>
										<DatePicker
											id="preDate"
											name="preDate"
											label="Pre Date"
											value={formState.values.preDate}
											onChange={value => _handleDateChange(value, 'preDate', 'date')}
											format="DD/MM/YYYY"
										/>
										<DatePicker
											id="postDate"
											name="postDate"
											label="Post Date"
											value={formState.values.postDate}
											onChange={value => _handleDateChange(value, 'postDate', 'date')}
											format="DD/MM/YYYY"
										/>
									</Grid> : null} */}
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div >
	);
};

ReportsAcceptanceDialogForm.propTypes = {
	className: PropTypes.string
};

export default ReportsAcceptanceDialogForm;
